import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TaskItem } from '../../types/tasks';
import { useTasks } from '../../context/TasksContext';
import { costTree, isRanked, money, unitSuggestions, valueLabel, valueOf } from '../../utils/projectValue';

/**
 * The ⋯ menu's Task value panel: what doing it gains (0.3 "s laptime", 4
 * "lb"), what it costs and how long it takes — so it's worth so much per
 * $1,000 and per hour. A unit typed once is offered again for the next task.
 * With no cost typed in, the cost is what's under it (materials, sub-tasks);
 * a typed cost is the whole figure and nothing under it is added.
 */
const ValuePanel: React.FC<{ item: TaskItem; readOnly: boolean; onClose: () => void }> = ({ item, readOnly, onClose }) => {
  const { items, costs, updateItem } = useTasks();
  const [benefit, setBenefit] = useState(item.benefit != null ? String(item.benefit) : '');
  const [unit, setUnit] = useState(item.benefit_unit || '');
  const [cost, setCost] = useState(item.cost != null ? String(item.cost) : '');
  const [hours, setHours] = useState(item.hours != null ? String(item.hours) : '');
  const [unitFocused, setUnitFocused] = useState(false);

  // What the materials and sub-tasks under it come to — its cost when it has
  // none of its own.
  const below = useMemo(
    () => costTree(items.map((i) => (i.id === item.id ? { ...i, cost: null } : i))).get(item.id) ?? 0,
    [items, item.id]
  );
  const suggestions = useMemo(() => unitSuggestions(items, unit), [items, unit]);

  const numOrNull = (s: string) => (s.trim() === '' || !Number.isFinite(Number(s)) ? null : Number(s));
  const patch = () => {
    const p: Partial<TaskItem> = {};
    const b = numOrNull(benefit);
    if (b !== (item.benefit ?? null)) p.benefit = b;
    if ((unit.trim() || null) !== (item.benefit_unit || null)) p.benefit_unit = unit.trim() || null;
    const c = numOrNull(cost);
    if (c !== (item.cost ?? null)) p.cost = c === null ? null : Math.max(0, c);
    const h = numOrNull(hours);
    if (h !== (item.hours ?? null)) p.hours = h === null ? null : Math.max(0, h);
    return p;
  };
  const save = () => {
    const p = patch();
    if (Object.keys(p).length) updateItem(item.id, p);
  };

  const handled = useRef(false);
  const latest = useRef(save);
  latest.current = save;
  useEffect(
    () => () => {
      if (!handled.current && !readOnly) latest.current();
    },
    [readOnly]
  );
  const finish = (fn: () => void) => () => {
    handled.current = true;
    fn();
    onClose();
  };

  // What it would come to with what's typed.
  const preview = valueOf(
    { ...item, benefit: numOrNull(benefit), benefit_unit: unit.trim() || null, cost: numOrNull(cost), hours: numOrNull(hours) },
    new Map(costs).set(item.id, numOrNull(cost) ?? below)
  );

  return (
    <div className="tk-menu-panel tp-value">
      <div className="tk-menu-title">Task value</div>
      <label className="tk-field">
        <span className="tk-field-label">Doing it gains</span>
        <span className="tp-value-gain">
          <input type="number" step="any" className="tk-input" value={benefit} placeholder="0.3" onChange={(e) => setBenefit(e.target.value)} disabled={readOnly} />
          <input
            className="tk-input"
            value={unit}
            placeholder="s laptime"
            onChange={(e) => setUnit(e.target.value)}
            onFocus={() => setUnitFocused(true)}
            onBlur={() => window.setTimeout(() => setUnitFocused(false), 150)}
            disabled={readOnly}
            aria-label="Unit"
          />
        </span>
      </label>
      {(unitFocused || !unit) && suggestions.length > 0 && !readOnly && (
        <div className="tk-quick-dates tp-value-units">
          {suggestions.map((u) => (
            <button key={u} type="button" className="tk-chip-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => setUnit(u)}>
              {u}
            </button>
          ))}
        </div>
      )}

      <div className="tp-material-row">
        <label className="tk-field">
          <span className="tk-field-label">Costs</span>
          <input type="number" min={0} step="0.01" className="tk-input" value={cost} placeholder="$" onChange={(e) => setCost(e.target.value)} disabled={readOnly} />
        </label>
        <label className="tk-field">
          <span className="tk-field-label">Takes (hours)</span>
          <input type="number" min={0} step="any" className="tk-input" value={hours} placeholder="hrs" onChange={(e) => setHours(e.target.value)} disabled={readOnly} />
        </label>
      </div>
      {below > 0 && (
        <p className="tk-menu-hint">
          {numOrNull(cost) === null
            ? `Costs ${money(below)} — the materials and sub-tasks under it. Type a cost to use that instead.`
            : `Using this cost. The ${money(below)} of materials and sub-tasks under it isn't added.`}
        </p>
      )}

      <p className="tp-plan-info">
        {isRanked(preview)
          ? <>Value: <strong>{valueLabel(preview)}</strong></>
          : numOrNull(benefit) === null || !unit.trim()
            ? 'Enter what it gains, and in what, to rank it on the Value tab.'
            : 'Add what it costs and/or the hours it takes to work out its value.'}
      </p>

      {!readOnly && (
        <div className="tk-menu-actions">
          {item.benefit != null && (
            <button type="button" className="tk-btn tk-btn-ghost" onClick={finish(() => updateItem(item.id, { benefit: null, benefit_unit: null }))}>
              Clear
            </button>
          )}
          <button type="button" className="tk-btn tk-btn-primary" onClick={finish(save)}>Save</button>
        </div>
      )}
    </div>
  );
};

export default ValuePanel;
